import {
  MAX_DEPTH,
  OUTCOME_TONES,
  type BeatChainDef,
  type CondDef,
  type HiddenVarDef,
  type OutcomeTableCase,
  type Tone,
} from './types';

/**
 * The beat editor's view of a chain: the path tree (a node per path, like the roll sheet's keys `b1/A/b2`), with depth, the roll-sheet
 * size, reach probabilities, and the reach-weighted tone mix (engine.html §5 "Admin: the beat editor").
 *
 * The probabilities are **expected values for the preview**: every hidden var and tell combination is enumerated from the var's
 * default case (its `else` row, or a pinned value), each option at a choice counts equally, and a condition on anything else (team,
 * ledger, raid facts…) uses `context` or counts as not holding. "Simulate rolls" runs the real shared-cs `BeatRoller` on the server for
 * exact numbers.
 */

export type OutcomeNode = {
  key: string;
  outcomeId: string;
  /** Unknown outcome ids (a typo in a table) show as missing. */
  missing: boolean;
  tone: Tone | '';
  /** P(this outcome | its option was chosen), 0..1. */
  p: number;
  /** P(reaching this outcome) from the root, 0..1. */
  reach: number;
  next?: BeatNode;
};

export type OptionNode = {
  key: string;
  optionId: string;
  label: string;
  table: string;
  /** A cutscene beat's single pseudo-option. */
  cutscene: boolean;
  toneNote: string;
  reach: number;
  outcomes: OutcomeNode[];
  /** Any good or windfall outcome anywhere under this option (every roll, every deeper beat). */
  hasGoodPath: boolean;
  /** Reach-weighted tone shares under this option (sums to 1 when anything is reachable). */
  toneMix: ToneMix;
};

export type BeatNode = {
  /** The path key (`b1`, `b1/A/b2`). */
  key: string;
  beatId: string;
  missing: boolean;
  /** Beats on the path including this one (shared-cs `ContentValidator.Structure` counts beats). */
  depth: number;
  tooDeep: boolean;
  /** This beat already appears higher on the path. */
  cycle: boolean;
  reach: number;
  options: OptionNode[];
};

export type ToneMix = Record<Tone, number>;

export type ChainAnalysis = {
  root?: BeatNode;
  maxDepth: number;
  /** Reachable (path, option) instances: the roll sheet's size. */
  rollSheetEntries: number;
  toneMix: ToneMix;
  /** Beat ids never reached from the root. */
  unreachableBeats: string[];
  /** Beat ids on a cycle. */
  cycles: string[];
  /** Any good or windfall outcome reachable at all. */
  hasGoodOutcome: boolean;
  /** Joint hidden-var combinations enumerated (capped). */
  combinations: number;
};

export type AnalysisOptions = {
  /** Pinned hidden var or tell values (`companion.bitten` → `true`). */
  pins?: Record<string, string>;
  /** Values for facts outside `var.*` (`team.size` → `3`). Unlisted facts count as not holding. */
  context?: Record<string, string>;
  /** The content's `story.maxDepth` (1..4); 4 is the hard cap. */
  maxDepth?: number;
};

export const emptyMix = (): ToneMix => ({ disaster: 0, bad: 0, mixed: 0, good: 0, windfall: 0 });
const isTone = (t: string | undefined): t is Tone =>
  (OUTCOME_TONES as readonly string[]).includes(t ?? '');
const MAX_COMBINATIONS = 4096;

// ----- condition evaluation over a hidden-var assignment ------------------------------------------------------------------------

const asNum = (v: string) =>
  v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : undefined;

function compare(actual: string, op: string, value: string, values: string[]): boolean {
  switch (op) {
    case 'eq':
    case 'ne': {
      const a = asNum(actual);
      const b = asNum(value);
      const eq = a !== undefined && b !== undefined ? a === b : actual === value;
      return op === 'eq' ? eq : !eq;
    }
    case 'gte':
    case 'lte': {
      const a = asNum(actual);
      const b = asNum(value);
      if (a === undefined || b === undefined) return false;
      return op === 'gte' ? a >= b : a <= b;
    }
    case 'in':
      return values.includes(actual);
    default:
      return false;
  }
}

/** Evaluates a condition with `var.*` from the assignment and every other fact from `context` (unknown → not holding). */
export function evalCond(
  c: CondDef | null | undefined,
  vars: Record<string, string>,
  context: Record<string, string> = {},
): boolean {
  if (c == null) return true;
  if (c.all?.length) return c.all.every((x) => evalCond(x, vars, context));
  if (c.any?.length) return c.any.some((x) => evalCond(x, vars, context));
  if (c.not) return !evalCond(c.not, vars, context);
  const fact = c.fact ?? '';
  if (!fact) return true;
  const key = (c.args ?? []).length ? `${fact}(${(c.args ?? []).join(',')})` : fact;
  const actual = fact.startsWith('var.') ? vars[fact.slice(4)] : (context[key] ?? context[fact]);
  if (actual === undefined) return false;
  return compare(actual, c.op ?? '', c.value ?? '', c.values ?? []);
}

/** A case matches when it's the else, has no condition, or its condition holds. */
const caseHolds = (
  k: { if?: CondDef | null; else?: boolean },
  vars: Record<string, string>,
  context: Record<string, string>,
) => !!k.else || k.if == null || evalCond(k.if, vars, context);

// ----- hidden var priors and the joint enumeration --------------------------------------------------------------------------------

/** A var's value distribution for the preview: the first case that holds without context (usually the else row). */
function varPrior(v: HiddenVarDef, context: Record<string, string>): [string, number][] {
  const cases = v.cases ?? [];
  const k = cases.find((c) => caseHolds(c, {}, context)) ?? cases[cases.length - 1];
  const picks = (k?.pick ?? []).filter((p) => (p.w ?? 1) > 0);
  const total = picks.reduce((s, p) => s + (p.w ?? 1), 0);
  if (!picks.length || total <= 0) return [[v.type === 'bool' ? 'false' : '', 1]];
  const merged = new Map<string, number>();
  for (const p of picks)
    merged.set(p.value ?? '', (merged.get(p.value ?? '') ?? 0) + (p.w ?? 1) / total);
  return [...merged];
}

type Assignment = { vars: Record<string, string>; p: number };

/** Every hidden-var and tell combination with its probability (pins fix a value). */
export function enumerateAssignments(
  chain: BeatChainDef,
  opts: AnalysisOptions = {},
): Assignment[] {
  const pins = opts.pins ?? {};
  const context = opts.context ?? {};
  let out: Assignment[] = [{ vars: {}, p: 1 }];
  for (const v of chain.hidden ?? []) {
    const id = v.id ?? '';
    const dist: [string, number][] = id in pins ? [[pins[id]!, 1]] : varPrior(v, context);
    const next: Assignment[] = [];
    for (const a of out)
      for (const [value, p] of dist)
        if (p > 0) next.push({ vars: { ...a.vars, [id]: value }, p: a.p * p });
    out = next;
    if (out.length > MAX_COMBINATIONS) out = out.slice(0, MAX_COMBINATIONS);
  }
  for (const t of chain.tells ?? []) {
    const id = t.id ?? '';
    const next: Assignment[] = [];
    for (const a of out) {
      let shown = Math.min(1, Math.max(0, (t.p ?? {})[a.vars[t.of ?? ''] ?? ''] ?? 0));
      if (id in pins) shown = pins[id] === 'true' ? 1 : 0;
      if (shown > 0) next.push({ vars: { ...a.vars, [id]: 'true' }, p: a.p * shown });
      if (shown < 1) next.push({ vars: { ...a.vars, [id]: 'false' }, p: a.p * (1 - shown) });
    }
    out = next;
    if (out.length > MAX_COMBINATIONS) out = out.slice(0, MAX_COMBINATIONS);
  }
  return out;
}

/** The outcome distribution of a table under one assignment: the first case that holds, then the weighted pick. */
export function tableDistribution(
  cases: OutcomeTableCase[] | undefined,
  vars: Record<string, string>,
  context: Record<string, string> = {},
): Map<string, number> {
  const out = new Map<string, number>();
  const k = (cases ?? []).find((c) => caseHolds(c, vars, context));
  const picks = (k?.pick ?? []).filter((p) => (p.w ?? 1) > 0);
  const total = picks.reduce((s, p) => s + (p.w ?? 1), 0);
  if (total <= 0) return out;
  for (const p of picks)
    out.set(p.outcome ?? '', (out.get(p.outcome ?? '') ?? 0) + (p.w ?? 1) / total);
  return out;
}

/** `BeatAnalysis.TableOutcomes`: every outcome a table can give under any roll (positive weights, first-seen order). */
export function tableOutcomes(cases: (OutcomeTableCase | null)[] | null | undefined): string[] {
  const seen: string[] = [];
  for (const c of cases ?? [])
    for (const p of c?.pick ?? [])
      if (p && (p.w ?? 1) > 0 && !seen.includes(p.outcome ?? '')) seen.push(p.outcome ?? '');
  return seen;
}

// ----- the path tree --------------------------------------------------------------------------------------------------------------

function buildBeat(
  chain: BeatChainDef,
  beatId: string,
  key: string,
  depthBefore: number,
  onPath: string[],
  maxDepth: number,
  seen: Set<string>,
  cycles: Set<string>,
): BeatNode {
  const beat = chain.beats?.[beatId];
  const node: BeatNode = {
    key,
    beatId,
    missing: !beat,
    depth: depthBefore,
    tooDeep: false,
    cycle: onPath.includes(beatId),
    reach: 0,
    options: [],
  };
  if (node.cycle) {
    cycles.add(beatId);
    return node;
  }
  if (!beat) return node;
  seen.add(beatId);
  const path = [...onPath, beatId];
  const choice = beat.choice;
  node.depth = depthBefore + 1; // beats on the path, like ContentValidator.Structure
  node.tooDeep = node.depth > maxDepth;
  const options = choice
    ? (choice.options ?? []).map((o) => ({
        id: o.id ?? '',
        label: o.label ?? '',
        table: o.table ?? '',
        toneNote: o.toneNote ?? '',
        outcomes: tableOutcomes(chain.tables?.[o.table ?? '']),
        cutscene: false,
      }))
    : [
        {
          id: '',
          label: '(cutscene)',
          table: '',
          toneNote: '',
          outcomes: beat.outcome ? [beat.outcome] : [],
          cutscene: true,
        },
      ];
  node.options = options.map((o) => {
    const optKey = o.cutscene ? key : `${key}/${o.id}`;
    const outcomes: OutcomeNode[] = o.outcomes.map((id) => {
      const def = chain.outcomes?.[id];
      const out: OutcomeNode = {
        key: `${optKey}:${id}`,
        outcomeId: id,
        missing: !def,
        tone: isTone(def?.tone) ? (def!.tone as Tone) : '',
        p: 0,
        reach: 0,
      };
      if (def?.next)
        out.next = buildBeat(
          chain,
          def.next,
          `${optKey}/${def.next}`,
          node.depth,
          path,
          maxDepth,
          seen,
          cycles,
        );
      return out;
    });
    return {
      key: optKey,
      optionId: o.id,
      label: o.label,
      table: o.table,
      cutscene: o.cutscene,
      toneNote: o.toneNote,
      reach: 0,
      outcomes,
      hasGoodPath: false,
      toneMix: emptyMix(),
    };
  });
  return node;
}

/** Spreads one assignment's probability mass through the tree (reach and conditional p accumulate over assignments). */
function propagate(
  chain: BeatChainDef,
  node: BeatNode,
  reach: number,
  a: Assignment,
  context: Record<string, string>,
  optReach: Map<string, number>,
) {
  node.reach += reach;
  if (!node.options.length) return;
  const each = reach / node.options.length;
  for (const o of node.options) {
    o.reach += each;
    optReach.set(o.key, (optReach.get(o.key) ?? 0) + a.p);
    const dist = o.cutscene
      ? new Map(o.outcomes.map((x) => [x.outcomeId, 1]))
      : tableDistribution(chain.tables?.[o.table], a.vars, context);
    for (const out of o.outcomes) {
      const p = dist.get(out.outcomeId) ?? 0;
      out.p += a.p * p; // normalised by the option's mass afterwards
      out.reach += each * p;
      if (out.next && p > 0) propagate(chain, out.next, each * p, a, context, optReach);
    }
  }
}

/** P(outcome | option): from the reach when the option is reachable, else from every assignment that visited it. */
function finishOption(o: OptionNode, optMass: Map<string, number>) {
  const mass = optMass.get(o.key) ?? 0;
  for (const out of o.outcomes)
    out.p = o.reach > 0 ? out.reach / o.reach : mass > 0 ? out.p / mass : 0;
}

/** Tone reach under a subtree, and whether any good path exists (structurally). */
function collectTones(o: OptionNode, mix: ToneMix): boolean {
  let good = false;
  for (const out of o.outcomes) {
    if (out.tone) mix[out.tone] += out.reach;
    if (out.tone === 'good' || out.tone === 'windfall') good = true;
    if (out.next) for (const child of out.next.options) if (collectTones(child, mix)) good = true;
  }
  return good;
}

export const normalizeMix = (mix: ToneMix): ToneMix => {
  const total = OUTCOME_TONES.reduce((s, t) => s + mix[t], 0);
  if (total <= 0) return emptyMix();
  const out = emptyMix();
  for (const t of OUTCOME_TONES) out[t] = mix[t] / total;
  return out;
};

function walk(node: BeatNode | undefined, fn: (n: BeatNode) => void) {
  if (!node) return;
  fn(node);
  for (const o of node.options) for (const out of o.outcomes) walk(out.next, fn);
}

/** Builds the path tree and its numbers for a chain. */
export function analyzeChain(chain: BeatChainDef, opts: AnalysisOptions = {}): ChainAnalysis {
  const maxDepth = Math.min(MAX_DEPTH, Math.max(1, opts.maxDepth ?? MAX_DEPTH));
  const seen = new Set<string>();
  const cycles = new Set<string>();
  const rootId = chain.root ?? '';
  const root = rootId ? buildBeat(chain, rootId, rootId, 0, [], maxDepth, seen, cycles) : undefined;
  const assignments = enumerateAssignments(chain, opts);
  const optMass = new Map<string, number>();
  if (root)
    for (const a of assignments) propagate(chain, root, a.p, a, opts.context ?? {}, optMass);

  let rollSheetEntries = 0;
  let hasGoodOutcome = false;
  const chainMix = emptyMix();
  walk(root, (n) => {
    for (const o of n.options) {
      rollSheetEntries++; // a cutscene beat's outcome is a roll-sheet entry too
      finishOption(o, optMass);
      const mix = emptyMix();
      o.hasGoodPath = collectTones(o, mix);
      o.toneMix = normalizeMix(mix);
      for (const out of o.outcomes) {
        if (out.tone) chainMix[out.tone] += out.reach;
        if (out.tone === 'good' || out.tone === 'windfall') hasGoodOutcome = true;
      }
    }
  });

  return {
    root,
    maxDepth,
    rollSheetEntries,
    toneMix: normalizeMix(chainMix),
    unreachableBeats: Object.keys(chain.beats ?? {}).filter((id) => !seen.has(id)),
    cycles: [...cycles],
    hasGoodOutcome,
    combinations: assignments.length,
  };
}

/** The deepest choice count on any path. */
export function deepest(root: BeatNode | undefined): number {
  let d = 0;
  walk(root, (n) => (d = Math.max(d, n.depth)));
  return d;
}

/** The distance (sum of absolute share differences, 0..2) between a mix and the target, for "far from target". */
export function mixDistance(mix: ToneMix, target: ToneMix): number {
  const t = normalizeMix(target);
  return OUTCOME_TONES.reduce((s, k) => s + Math.abs(mix[k] - t[k]), 0);
}
